import React, { useEffect, useRef, useState } from 'react';
import { createChart } from 'lightweight-charts';
import { COMMODITIES, SVG_ICONS } from './config';

const getChartSymbol = (key) => {
  const config = COMMODITIES[key];
  if (config?.yahooSymbol) return config.yahooSymbol;
  const lmeFallback = { zinc: 'ZN=F', nickel: 'NI=F', lead: 'PB=F' };
  return lmeFallback[key] || null;
};

const fetchHistoricalData = async (symbol, range) => {
  const interval = ['1mo', '3mo', '6mo'].includes(range) ? '1d' : (['1y', '2y'].includes(range) ? '1d' : '1wk');
  // Uses local CRA dev proxy → https://query1.finance.yahoo.com
  const url = `/api/yahoo/v8/finance/chart/${encodeURIComponent(symbol)}?range=${range}&interval=${interval}`;

  try {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), 10000);
    const resp = await fetch(url, { signal: controller.signal });
    clearTimeout(id);
    if (!resp.ok) return null;
    const json = await resp.json();
    const result = json?.chart?.result?.[0];
    if (!result) return null;

    const timestamps = result.timestamp;
    const quote = result.indicators.quote[0];
    const ohlcData = [];
    for (let i = 0; i < timestamps.length; i++) {
      if (quote.open[i] == null || quote.close[i] == null) continue;
      ohlcData.push({
        time: timestamps[i],
        open:  +quote.open[i].toFixed(2),
        high:  +quote.high[i].toFixed(2),
        low:   +quote.low[i].toFixed(2),
        close: +quote.close[i].toFixed(2),
      });
    }
    return ohlcData;
  } catch (e) {
    return null;
  }
};

const getChartThemeColors = (isDark) => {
  return isDark ? {
    bg: '#0a0f1c',
    text: '#8a9ab5',
    grid: 'rgba(40,48,64,0.3)',
    border: 'rgba(40,48,64,0.5)',
    upColor: '#30b86a',
    downColor: '#d94848',
    wickUp: '#30b86a',
    wickDown: '#d94848',
    crosshairColor: 'rgba(232,128,64,0.4)',
  } : {
    bg: '#ffffff',
    text: '#6b6158',
    grid: 'rgba(226,223,219,0.5)',
    border: 'rgba(226,223,219,0.8)',
    upColor: '#25a05a',
    downColor: '#d93636',
    wickUp: '#25a05a',
    wickDown: '#d93636',
    crosshairColor: 'rgba(240,112,32,0.4)',
  };
};

export default function ChartModal({ chartKey, onClose, theme }) {
  const chartContainerRef = useRef(null);
  const [range, setRange] = useState('1y');
  const [dataLabel, setDataLabel] = useState('');
  const [loading, setLoading] = useState(true);
  const chartInstanceRef = useRef(null);
  const isDark = theme === 'dark';

  const config = COMMODITIES[chartKey];
  const symbol = getChartSymbol(chartKey);

  useEffect(() => {
    let active = true;

    const loadChart = async () => {
      setLoading(true);
      if (chartInstanceRef.current) {
        chartInstanceRef.current.remove();
        chartInstanceRef.current = null;
      }

      if (!symbol) {
        setLoading(false);
        return;
      }

      const data = await fetchHistoricalData(symbol, range);
      if (!active) return;

      if (!data || data.length === 0) {
        setLoading(false);
        return;
      }

      const colors = getChartThemeColors(isDark);

      const chartInstance = createChart(chartContainerRef.current, {
        width: chartContainerRef.current.clientWidth,
        height: chartContainerRef.current.clientHeight,
        layout: {
          background: { type: 'solid', color: colors.bg },
          textColor: colors.text,
          fontFamily: "'Inter', 'Plus Jakarta Sans', system-ui, sans-serif",
          fontSize: 12,
        },
        grid: {
          vertLines: { color: colors.grid },
          horzLines: { color: colors.grid },
        },
        crosshair: {
          mode: 0,
          vertLine: { color: colors.crosshairColor, width: 1, style: 2 },
          horzLine: { color: colors.crosshairColor, width: 1, style: 2 },
        },
        rightPriceScale: {
          borderColor: colors.border,
          scaleMargins: { top: 0.1, bottom: 0.1 },
        },
        timeScale: {
          borderColor: colors.border,
          timeVisible: false,
        },
        handleScroll: true,
        handleScale: true,
      });

      const chartSeries = chartInstance.addCandlestickSeries({
        upColor: colors.upColor,
        downColor: colors.downColor,
        borderDownColor: colors.downColor,
        borderUpColor: colors.upColor,
        wickDownColor: colors.wickDown,
        wickUpColor: colors.wickUp,
      });

      chartSeries.setData(data);
      chartInstance.timeScale().fitContent();

      chartInstanceRef.current = chartInstance;

      const first = new Date(data[0].time * 1000).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
      const last = new Date(data[data.length - 1].time * 1000).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
      setDataLabel(`${first} — ${last} · ${data.length} candles`);
      setLoading(false);
    };

    if (active) {
      loadChart();
    }

    const handleResize = () => {
      if (chartInstanceRef.current && chartContainerRef.current) {
        chartInstanceRef.current.applyOptions({
          width: chartContainerRef.current.clientWidth,
          height: chartContainerRef.current.clientHeight
        });
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      active = false;
      window.removeEventListener('resize', handleResize);
      if (chartInstanceRef.current) {
        chartInstanceRef.current.remove();
        chartInstanceRef.current = null;
      }
    };
  }, [chartKey, range, isDark, symbol]);

  if (!chartKey) return null;

  return (
    <div className="chart-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="chart-modal relative flex flex-col pt-0">
        <div className="chart-modal-header">
          <div>
            <div className="chart-modal-title" id="chart-title">{config.name} — COMEX Historical</div>
            <div className="chart-modal-sub" id="chart-sub">{config.yahooSymbol || chartKey.toUpperCase()} · COMEX Futures</div>
          </div>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <div className="chart-timeframe-tabs items-center" id="chart-timeframes">
              {['1mo', '3mo', '6mo', '1y', '5y', '10y', 'max'].map((r) => (
                <button
                  key={r}
                  className={`tf-btn ${range === r ? 'active' : ''}`}
                  onClick={() => setRange(r)}
                >
                  {r === '1mo' ? '1M' : r === '3mo' ? '3M' : r === '6mo' ? '6M' : r === '1y' ? '1Y' : r === '5y' ? '5Y' : r === '10y' ? '10Y' : 'Max'}
                </button>
              ))}
            </div>
            <button className="chart-close-btn flex items-center justify-center p-2" onClick={onClose}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '18px', height: '18px' }}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
          </div>
        </div>
        <div className="chart-container relative" ref={chartContainerRef}>
          {loading && <div className="chart-loading absolute inset-0 text-slate-400 bg-slate-900/90 z-10 flex items-center justify-center">{symbol ? 'Loading chart data...' : 'Chart not available for LME-only metals'}</div>}
        </div>
        <div className="chart-footer">
          <span>Data: Yahoo Finance (COMEX Futures) · For illustrative purposes only</span>
          <span id="chart-data-range">{dataLabel}</span>
        </div>
      </div>
    </div>
  );
}
